import React, { useState } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Lock,
  Clock,
  CreditCard,
  Check,
  Users,
  Building2,
  ArrowRight,
  Mail,
  Phone,
  Zap
} from 'lucide-react';

const PRICING = {
  monthly: {
    price: 99,
    label: 'Mensuel',
    commitment: 'Sans engagement',
    description: '99€ HT/mois par licence'
  },
  annual: {
    price: 75,
    label: 'Annuel',
    commitment: '12 mois',
    description: '75€ HT/mois (900€ HT/an)',
    savings: 24
  }
};

const TrialExpiredOverlay = ({ companyName, onLogout }) => {
  const [selectedPlan, setSelectedPlan] = useState('annual');
  const [licenseCount, setLicenseCount] = useState(1);
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const response = await api.buyLicenses({
        nombre_licences: licenseCount,
        billing_plan: selectedPlan,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error initiating subscription:', error);
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    const pricing = PRICING[selectedPlan];
    const monthly = pricing.price * licenseCount;
    
    if (selectedPlan === 'annual') {
      return {
        monthly,
        total: monthly * 12,
        label: `${monthly}€ HT/mois`
      };
    }
    return {
      monthly,
      total: monthly,
      label: `${monthly}€ HT/mois`
    };
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-900/95 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-amber-500/20 rounded-full mb-4">
            <Clock className="w-10 h-10 text-amber-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Votre essai gratuit est terminé
          </h1>
          <p className="text-slate-400 text-lg">
            {companyName ? `${companyName} - ` : ''}Vos données sont conservées et vous attendent
          </p>
        </div>

        {/* Subscription Card */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="text-center border-b border-slate-700">
            <CardTitle className="text-white flex items-center justify-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-400" />
              Choisissez votre abonnement
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Plan Selection */}
            <div className="grid grid-cols-2 gap-4">
              {/* Monthly */}
              <button
                onClick={() => setSelectedPlan('monthly')}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  selectedPlan === 'monthly'
                    ? 'border-emerald-500 bg-emerald-500/10'
                    : 'border-slate-600 hover:border-slate-500'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-white">{PRICING.monthly.label}</span>
                  {selectedPlan === 'monthly' && (
                    <Check className="w-5 h-5 text-emerald-400" />
                  )}
                </div>
                <p className="text-2xl font-bold text-white">
                  {PRICING.monthly.price}€
                  <span className="text-sm font-normal text-slate-400">/mois</span>
                </p>
                <p className="text-xs text-slate-400 mt-1">{PRICING.monthly.commitment}</p>
              </button>

              {/* Annual */}
              <button
                onClick={() => setSelectedPlan('annual')}
                className={`p-4 rounded-lg border-2 text-left transition-all relative ${
                  selectedPlan === 'annual'
                    ? 'border-emerald-500 bg-emerald-500/10'
                    : 'border-slate-600 hover:border-slate-500'
                }`}
              >
                <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-emerald-500 text-white text-xs font-bold rounded-full">
                  -{PRICING.annual.savings}%
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-white">{PRICING.annual.label}</span>
                  {selectedPlan === 'annual' && (
                    <Check className="w-5 h-5 text-emerald-400" />
                  )}
                </div>
                <p className="text-2xl font-bold text-white">
                  {PRICING.annual.price}€
                  <span className="text-sm font-normal text-slate-400">/mois</span>
                </p>
                <p className="text-xs text-slate-400 mt-1">{PRICING.annual.commitment}</p>
              </button>
            </div>

            {/* License Count */}
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Nombre de licences (agents)</label>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setLicenseCount(Math.max(1, licenseCount - 1))}
                  className="w-10 h-10 rounded-lg bg-slate-700 text-white hover:bg-slate-600 transition"
                  disabled={licenseCount <= 1}
                >
                  -
                </button>
                <span className="text-3xl font-bold text-white w-16 text-center">
                  {licenseCount}
                </span>
                <button
                  onClick={() => setLicenseCount(licenseCount + 1)}
                  className="w-10 h-10 rounded-lg bg-slate-700 text-white hover:bg-slate-600 transition"
                >
                  +
                </button>
                <div className="flex items-center gap-2 text-slate-400 ml-4">
                  <Users className="w-4 h-4" />
                  <span className="text-sm">1 licence = 1 agent commercial</span>
                </div>
              </div>
            </div>

            {/* Total */}
            <div className="p-4 bg-slate-700/50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-slate-300">Total</span>
                <div className="text-right">
                  <span className="text-2xl font-bold text-white">
                    {calculateTotal().label}
                  </span>
                  {selectedPlan === 'annual' && (
                    <p className="text-sm text-emerald-400">
                      soit {calculateTotal().total}€ HT/an
                    </p>
                  )}
                </div>
              </div>
              {selectedPlan === 'annual' && (
                <p className="text-sm text-emerald-400 mt-2 flex items-center gap-1">
                  <Zap className="w-4 h-4" />
                  Économie de {(99 - 75) * licenseCount * 12}€ HT/an
                </p>
              )}
            </div>

            {/* Subscribe Button */}
            <Button
              onClick={handleSubscribe}
              disabled={loading}
              className="w-full h-14 text-lg bg-emerald-600 hover:bg-emerald-700"
            >
              {loading ? (
                'Redirection vers le paiement...'
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  S'abonner maintenant
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>

            {/* Features */}
            <div className="pt-4 border-t border-slate-700">
              <p className="text-sm text-slate-400 mb-3">Inclus dans votre abonnement :</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  'Accès illimité à la plateforme',
                  'Recherche RNIC & DPE',
                  'Calcul prime CEE automatique',
                  'Pipeline commercial Kanban',
                  'Gamification agents',
                  'Support prioritaire'
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-emerald-500" />
                    {feature}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-6 flex items-center justify-between text-slate-400">
          <div className="flex items-center gap-4 text-sm">
            <a href="mailto:support@coproenergy.fr" className="flex items-center gap-1 hover:text-white transition">
              <Mail className="w-4 h-4" />
              Contactez-nous
            </a>
            <span>|</span>
            <a href="tel:+33100000000" className="flex items-center gap-1 hover:text-white transition">
              <Phone className="w-4 h-4" />
              Assistance
            </a>
          </div>
          <button
            onClick={onLogout}
            className="text-sm text-slate-400 hover:text-white transition"
          >
            Se déconnecter
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrialExpiredOverlay;
